import { useCallback, useMemo } from 'react';
import { useDisclosure } from '@shared/hooks';
import { useToast } from '@retail/hooks';
import { useAppTFunction } from '@retail/app/i18n';
import { generateXLSX, XLSXRow } from '@retail/products/utils';
import { AssortmentPrices } from '@retail/my-assortment/types';
import { AssortmentExportColumn } from './AssortmentExportColumn';
import { columnHeaders } from './columnHeaders';
import { assortmentPricesValueMap } from './assortmentPricesValueMap';

interface Props {
  fileName: string;
  items: AssortmentPrices[];
  columns: AssortmentExportColumn[];
}

export const useExportAssortmentItemsToExcel = ({ fileName, items, columns }: Props) => {
  const t = useAppTFunction();
  const toast = useToast();
  const { isOpen: isLoading, onOpen: onLoadingStart, onClose: onLoadingEnd } = useDisclosure(false);

  const exportToExcel = useCallback(async () => {
    onLoadingStart();

    const headerRow: XLSXRow = columns.map((column) => columnHeaders(t)[column]);

    const dataRows: XLSXRow[] = items.map((item) =>
      columns.map((column) => assortmentPricesValueMap[column](item, t))
    );

    await generateXLSX({
      rows: [headerRow, ...dataRows],
      fileName,
    });

    toast.success(t('export.messages.fileDownloaded'));

    onLoadingEnd();
  }, [columns, fileName, items, onLoadingEnd, onLoadingStart, t, toast]);

  return useMemo(
    () => ({
      isLoading,
      exportToExcel,
    }),
    [exportToExcel, isLoading]
  );
};
